import { z } from 'zod';

/**
 * Reservierungs-Status Enum.
 * Workflow: pending → (confirmed | cancelled | expired)
 */
export const ReservationStatusSchema = z.enum(['pending', 'confirmed', 'cancelled', 'expired']);

/** TypeScript Type für Reservierungs-Status */
export type ReservationStatus = z.infer<typeof ReservationStatusSchema>;

/**
 * Zod Schema für Reservation — Runtime-Validierung + Type-Inference.
 * Ticket-Reservierung vor PDF-Versand.
 */
export const ReservationSchema = z.object({
  id: z.string().cuid(),
  eventId: z.string().cuid(),
  userId: z.string(),
  quantity: z.number().int().positive().default(1),
  status: ReservationStatusSchema.default('pending'),
  expiresAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/** TypeScript Type — abgeleitet aus dem Zod Schema */
export type Reservation = z.infer<typeof ReservationSchema>;

/**
 * Schema für Reservation-Erstellung.
 */
export const CreateReservationSchema = ReservationSchema.pick({
  eventId: true,
  userId: true,
  quantity: true,
  status: true,
  expiresAt: true,
});

/** TypeScript Type für Reservation-Erstellung */
export type CreateReservation = z.infer<typeof CreateReservationSchema>;

/**
 * Schema für Reservation-Update.
 * Alle Felder optional — PATCH-Semantik.
 */
export const UpdateReservationSchema = ReservationSchema.pick({
  quantity: true,
  status: true,
  expiresAt: true,
}).partial();

/** TypeScript Type für Reservation-Update */
export type UpdateReservation = z.infer<typeof UpdateReservationSchema>;

/**
 * Schema für den kombinierten "Fan anlegen + Tickets zuweisen" Endpunkt.
 * Wird von POST /events/:eventId/assign-fan validiert.
 */
export const AssignFanSchema = z.object({
  /** Vorname des Fans (Pflicht) */
  firstName: z.string().min(1, 'Vorname darf nicht leer sein').max(100),
  /** Nachname des Fans (optional) */
  lastName: z.string().max(100).optional(),
  /** E-Mail-Adresse (Pflicht, wird auf Duplikate geprüft) */
  email: z.string().email('Ungültige E-Mail-Adresse'),
  /** Anzahl der zu reservierenden Tickets (min 1) */
  quantity: z.number().int().min(1, 'Mindestens 1 Ticket erforderlich'),
});

/** TypeScript Type für Fan-Anlegen + Ticketzuweisung */
export type AssignFan = z.infer<typeof AssignFanSchema>;

/**
 * Antwort-Schema von POST /events/:eventId/assign-fan.
 */
export const AssignFanResponseSchema = z.object({
  /** ID des angelegten oder gefundenen Users */
  userId: z.string(),
  /** Ob der User neu angelegt wurde */
  userCreated: z.boolean(),
  /** ID der erstellten Reservation */
  reservationId: z.string(),
  /** Anzahl zugewiesener Tickets (0 wenn noch keine PDFs vorhanden) */
  ticketsAssigned: z.number().int().nonnegative(),
  /** Ob die Ticket-Mail versandt wurde */
  mailSent: z.boolean(),
});

/** TypeScript Type für die Antwort des assign-fan Endpunkts */
export type AssignFanResponse = z.infer<typeof AssignFanResponseSchema>;

/**
 * Feste Self-Cancel-Frist in Stunden vor Event-Beginn.
 * Nutzer koennen ihre Anmeldung/Reservierung bis zu diesem Zeitpunkt stornieren.
 */
export const DEFAULT_SELF_CANCEL_HOURS = 24;
